<script setup lang="ts">
import { computed, ref } from 'vue'
import {
  deleteServiceCategoryAction,
  getServiceCategoriesAction,
  toggleServiceCategoryActivationAction,
} from '@/api/functions/settings/service-categories-action'
import { useMutationApi, useQueryApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { settingsTabs } from '@/constants/setting-tabs.ts'
import { getErrorMessage } from '@/utils/errorUtils.ts'
import SettingsOverview from '../partials/SettingsOverview.vue'
import CreateCategoryModal from './partials/CreateCategoryModal.vue'
import UpdateCategoryModal from './partials/UpdateCategoryModal.vue'

const showToggleModal = ref(false)
const showDeleteModal = ref(false)
const showUpdateModal = ref(false)
const showCreateModal = ref(false)
const toast = useToast()

const selectedCategory = ref<null | { id: string }>(null)
const selectedToggleCategory = ref<any>(null)
const currentPage = ref(1)
const itemsPerPage = 10

const { data: categoriesData, refetch: refetchCategories } = useQueryApi({
  queryKey: ['service-categories'],
  queryFn: () => getServiceCategoriesAction(),
})

const categories = computed(() => categoriesData.value?.data || [])

const totalItems = computed(() => categories.value.length)

const categoryRows = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage
  const end = start + itemsPerPage

  return categories.value.slice(start, end).map((item, idx) => ({
    ...item,
    index: start + idx + 1,
    title: item.name,
    slug: item.slug,
    statusLabel: item.isActive ? 'فعال' : 'غیرفعال',
  }))
})

const columns = [
  {
    key: 'index',
    label: '#',
    class: 'col-span-1 font-fa text-sm',
  },
  {
    key: 'title',
    label: 'عنوان',
    class: 'col-span-3 text-sm',
  },
  {
    key: 'slug',
    label: 'اسلاگ',
    class: 'col-span-2 text-[13px]',
  },
  {
    key: 'statusLabel',
    label: 'وضعیت',
    class: 'col-span-1 text-sm',
  },
]

const actions = [
  {
    label: (row: any) => (row.isActive ? 'غیرفعال کردن' : 'فعال کردن'),
    handlerName: 'toggleCategoryActivation',
  },
  {
    label: 'ویرایش',
    handlerName: 'editCategory',
  },
  {
    label: 'حذف',
    handlerName: 'deleteCategory',
  },
]

const onEditCategory = (category: any) => {
  selectedCategory.value = category
  showUpdateModal.value = true
}

const onToggleCategoryActivation = (category: any) => {
  if (!category?.id) {
    toast.error('دسته‌بندی انتخاب نشده است', 'خطا')
    return
  }

  selectedToggleCategory.value = category
  showToggleModal.value = true
}

const onDeleteCategory = (category: any) => {
  if (!category?.id) {
    toast.error('دسته‌بندی انتخاب نشده است', 'خطا')
    return
  }

  selectedCategory.value = category
  showDeleteModal.value = true
}

const deleteCategoryMutation = useMutationApi({
  mutationFn: (id: string) => deleteServiceCategoryAction(id),
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا در حذف دسته‌بندی')
  },
})

const toggleCategoryActivationMutation = useMutationApi({
  mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
    toggleServiceCategoryActivationAction(id, is_active),
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا در تغییر وضعیت دسته‌بندی')
  },
})

const handleToggleCategoryActivation = async () => {
  if (!selectedToggleCategory.value?.id) {
    toast.error('دسته‌بندی انتخاب نشده است', 'خطا')
    return
  }

  const category = selectedToggleCategory.value

  try {
    await toggleCategoryActivationMutation.mutateAsync({
      id: category.id,
      is_active: !category.isActive,
    })

    toast.success(category.isActive ? 'دسته‌بندی غیرفعال شد' : 'دسته‌بندی فعال شد')

    showToggleModal.value = false
    selectedToggleCategory.value = null

    await refetchCategories()
  } catch {}
}

const handleDeleteCategory = async () => {
  if (!selectedCategory.value?.id) {
    toast.error('دسته‌بندی انتخاب نشده است', 'خطا')
    return
  }

  try {
    await deleteCategoryMutation.mutateAsync(selectedCategory.value.id)

    toast.success('دسته‌بندی با موفقیت حذف شد')

    showDeleteModal.value = false
    selectedCategory.value = null

    await refetchCategories()
  } catch {}
}

const handleCategoryUpdated = async () => {
  showUpdateModal.value = false
  selectedCategory.value = null

  await refetchCategories()
}

const handleCategoryCreated = async () => {
  showCreateModal.value = false

  await refetchCategories()
}

const goToPage = (page: number) => {
  currentPage.value = page
}
</script>

<template>
  <div class="mb-12 w-full">
    <!-- Toggle Activation -->
    <ConfirmModal v-model="showToggleModal" :title="selectedToggleCategory?.isActive
      ? 'غیرفعال کردن دسته‌بندی'
      : 'فعال کردن دسته‌بندی'" :message="selectedToggleCategory?.isActive
        ? 'آیا از غیرفعال کردن این دسته‌بندی اطمینان دارید؟'
        : 'آیا از فعال کردن این دسته‌بندی اطمینان دارید؟'" :confirm-text="selectedToggleCategory?.isActive
          ? 'بله غیرفعال شود'
          : 'بله فعال شود'" cancel-text="بازگشت" :loading="toggleCategoryActivationMutation.isPending.value"
      :disabled="toggleCategoryActivationMutation.isPending.value" @confirm="handleToggleCategoryActivation" @cancel="
        showToggleModal = false;
      selectedToggleCategory = null;
      " />
    <!-- Delete Category -->
    <ConfirmModal v-model="showDeleteModal" title="حذف کردن دسته‌بندی" message="آیا از حذف این دسته‌بندی اطمینان دارید؟"
      confirm-text="بله حذف شود" cancel-text="بازگشت" :loading="deleteCategoryMutation.isPending.value"
      :disabled="deleteCategoryMutation.isPending.value" @confirm="handleDeleteCategory"
      @cancel="showDeleteModal = false" />

    <!-- Update Category -->
    <UpdateCategoryModal v-model="showUpdateModal" :selectedCategory="selectedCategory as unknown as Record<string, string>
      " @confirm="handleCategoryUpdated" @cancel="showUpdateModal = false" />

    <!-- Create Category -->
    <CreateCategoryModal v-model="showCreateModal" @confirm="handleCategoryCreated" @cancel="showCreateModal = false" />
    <SettingsOverview :tabs="settingsTabs">
      <template #actions>
        <div class="flex items-center gap-2 pb-1">
          <Button :isAdmin="true" class="h-8! rounded-lg! px-3! py-0 text-[13px] font-medium"
            @click="showCreateModal = true">
            افزودن دسته‌بندی جدید
          </Button>
        </div>
      </template>
    </SettingsOverview>
    <div class="mt-20 lg:mt-7">
      <Table :columns="columns" :rows="categoryRows" :actions="actions" @primaryAction="onToggleCategoryActivation"
        @secondaryAction="onEditCategory" @thirdAction="onDeleteCategory" />
      <Pagination v-if="totalItems > 0" v-model:currentPage="currentPage" :itemsPerPage="itemsPerPage"
        :totalItems="totalItems" :isAdmin="true" @update:currentPage="goToPage" />
    </div>
  </div>
</template>